import type { MapDef } from '../world/map';
import { T } from '../world/tiles';
import { fbm, hash2, hex, mix, noise2, scale, smoothstep, type RGB } from './noise';

/**
 * Ground materials of the kaohsiung package. Tile chars reserved for it: '@', '$' walkable, '%' solid
 * (solidity is declared in src/world/tiles.ts). Runs inside the ground worker, so keep it pure: no DOM,
 * no game state. Return null for chars you don't own.
 *
 *  '@'  sunny pavers: pale granite slabs on the station plaza, red brick on the Lotus Pond shore,
 *       mosaic terrazzo on the Formosa Boulevard plazas, old concrete with embedded rails at Pier-2,
 *       quay concrete with a yellow safety line by the water; warm beige pavers elsewhere
 *  '$'  the light-rail grass track (turf with two steel rails); inside Liuhe it is the market's asphalt
 *       with painted stall pitches
 *  '%'  flat roofs of the buildings (concrete with a parapet, gravel, stains)
 */

type R = readonly [number, number, number, number];
const STATION: R = [1, 1, 30, 15];
const LOTUS: R = [48, 1, 47, 15];
const FORMOSA: R[] = [[38, 19, 5, 9], [48, 19, 5, 9]];
const PIER2: R = [1, 38, 42, 8];
const QUAY: R = [0, 48, 96, 2];
const LIUHE: R = [53, 23, 25, 4];
/** Tile row of the old Hamasen rails embedded in the Pier-2 concrete. */
const RAIL_ROW = 43;

const inRect = (tx: number, ty: number, r: R) => tx >= r[0] && ty >= r[1] && tx < r[0] + r[2] && ty < r[1] + r[3];
const at = (map: MapDef, tx: number, ty: number) => (tx < 0 || ty < 0 || tx >= map.w || ty >= map.h ? ' ' : map.rows[ty][tx]);

const K = {
  paverA: hex('#dccbb0'),
  paverB: hex('#c9b69a'),
  paverJoint: hex('#a08e74'),
  graniteA: hex('#e2e0da'),
  graniteB: hex('#c8c6c0'),
  graniteJoint: hex('#8e8c88'),
  brickA: hex('#b85a40'),
  brickB: hex('#9a4a36'),
  brickC: hex('#c87a58'),
  brickJoint: hex('#d8c8b0'),
  terrazzo: hex('#d6d2c8'),
  concrete: hex('#b4b0a6'),
  concreteDark: hex('#8e8a80'),
  rail: hex('#6a5a4a'),
  railShine: hex('#c8b8a0'),
  turfA: hex('#5f9a46'),
  turfB: hex('#4a7f38'),
  steel: hex('#9aa0a8'),
  steelShine: hex('#e8ecf0'),
  asphalt: hex('#4a4a50'),
  asphaltLight: hex('#5e5e64'),
  white: hex('#f2eee4'),
  yellow: hex('#f2c230'),
  red: hex('#c8402e'),
  roof: hex('#9a9690'),
  roofLight: hex('#b8b4ac'),
  roofDark: hex('#6e6a64'),
};

/** Warm beige pavers in running bond, sun-bleached, with a darker joint. */
function pavers(x: number, y: number): RGB {
  const rowH = 8, w = 12;
  const row = Math.floor(y / rowH), off = (row % 2) * 6;
  const col = Math.floor((x + off) / w);
  const fx = x + off - col * w, fy = y - row * rowH;
  const tone = hash2(col, row, 501);
  let c = mix(K.paverA, K.paverB, tone * 0.6 + noise2(x * 0.25, y * 0.25, 502) * 0.4);
  if (hash2(Math.floor(x), Math.floor(y), 503) < 0.03) c = scale(c, 0.9);
  if (fx < 0.8 || fy < 0.8) c = mix(c, K.paverJoint, 0.7);
  else if (fy < 1.6 || fx < 1.6) c = scale(c, 1.05);
  return c;
}

/** Big pale granite slabs (16 px) with a dark joint, a few darker slabs and a yellow tactile strip. */
function granite(x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  const tone = hash2(tx, ty, 511);
  let c = mix(K.graniteA, K.graniteB, tone * 0.5 + fbm(x / 9, y / 9, 512) * 0.5);
  if (tone > 0.86) c = scale(c, 0.9);
  // Speckle.
  const sp = hash2(Math.floor(x * 2), Math.floor(y * 2), 513);
  if (sp < 0.05) c = scale(c, 0.82);
  else if (sp > 0.96) c = scale(c, 1.08);
  if (fx < 0.9 || fy < 0.9) c = mix(c, K.graniteJoint, 0.6);
  // Tactile paving strip from the doors to the taxi rank (tile column 16).
  if (tx === 16 && ty >= 8 && ty <= 11 && fx > 3 && fx < 13) {
    const bump = ((Math.floor((fx - 3) / 2.5) + Math.floor(fy / 2.5)) % 2) === 0;
    return bump ? K.yellow : scale(K.yellow, 0.85);
  }
  return c;
}

/** Red brick pavers in a herringbone-ish basket weave, pale mortar, moss in the shade of the shore. */
function brick(map: MapDef, x: number, y: number): RGB {
  const s = 6;
  const bx = Math.floor(x / (s * 2)), by = Math.floor(y / s);
  const horizontal = (bx + by) % 2 === 0;
  const fx = x - bx * s * 2, fy = y - by * s;
  let c = [K.brickA, K.brickB, K.brickC][Math.floor(hash2(bx, by, 521) * 3)];
  c = mix(c, K.brickB, noise2(x * 0.3, y * 0.3, 522) * 0.3);
  const joint = horizontal ? fy < 0.8 || fx < 0.8 : fx < 0.8 || Math.abs(fx - s) < 0.8 || fy < 0.8;
  if (joint) c = mix(c, K.brickJoint, 0.65);
  // Moss and dust near the water's edge.
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  if (at(map, tx, ty - 1) === '~' || at(map, tx - 1, ty) === '~' || at(map, tx + 1, ty) === '~') c = mix(c, hex('#5a7a3a'), fbm(x / 5, y / 5, 523) * 0.35);
  return c;
}

/** Terrazzo with coloured chips: the mosaic plazas around Formosa Boulevard's glass shells. */
function terrazzo(x: number, y: number): RGB {
  let c = mix(K.terrazzo, scale(K.terrazzo, 0.92), noise2(x * 0.3, y * 0.3, 531));
  const g = 2.2;
  const gx = Math.floor(x / g), gy = Math.floor(y / g);
  const r = hash2(gx, gy, 532);
  if (r < 0.1) {
    const chips = ['#c8402e', '#2f7fd9', '#f2c230', '#2fa36a', '#8a4aa8', '#ffffff'];
    const d = Math.hypot(x - (gx + 0.5) * g, y - (gy + 0.5) * g);
    if (d < 0.9) c = mix(c, hex(chips[Math.floor(hash2(gx, gy, 533) * chips.length)]), 0.85);
  }
  // Big 32 px slabs with a fine joint.
  const fx = ((x % 32) + 32) % 32, fy = ((y % 32) + 32) % 32;
  if (fx < 0.7 || fy < 0.7) c = scale(c, 0.78);
  return c;
}

/** Old port concrete: large cracked slabs, rust stains, and two embedded rails in the rail row. */
function portConcrete(x: number, y: number): RGB {
  const ty = Math.floor(y / T);
  const fy = y - ty * T;
  let c = mix(K.concrete, K.concreteDark, fbm(x / 14, y / 14, 541) * 0.6);
  const sx = ((x % 48) + 48) % 48, sy = ((y % 32) + 32) % 32;
  if (sx < 1 || sy < 1) c = scale(c, 0.72);
  // Cracks: thin dark wandering lines.
  const crack = Math.abs(Math.sin(x / 7 + fbm(x / 10, y / 10, 542) * 9) + (y % 32) / 16 - 1);
  if (crack < 0.035 && hash2(Math.floor(x / 20), Math.floor(y / 20), 543) < 0.5) c = scale(c, 0.6);
  // Rust blooms.
  const rust = fbm(x / 11, y / 11, 544);
  if (rust > 0.68) c = mix(c, hex('#9a5a34'), (rust - 0.68) * 1.8);
  if (ty === RAIL_ROW) {
    for (const ry of [4.5, 11.5]) {
      const d = Math.abs(fy - ry);
      if (d < 0.6) return mix(K.railShine, K.rail, 0.5 + noise2(x * 0.5, 0, 545) * 0.3);
      if (d < 1.4) return K.rail;
      if (d < 2.2) c = scale(c, 0.86);
    }
    // Sleepers between the rails.
    if (fy > 5.8 && fy < 10.2 && ((x % 10) + 10) % 10 < 3) c = mix(c, hex('#6a5a48'), 0.5);
  }
  // Gravel grain.
  if (hash2(Math.floor(x), Math.floor(y), 546) < 0.05) c = scale(c, 0.88);
  return c;
}

/** Quay concrete: slabs, a yellow safety line and wet darkening along the sea edge, mooring stains. */
function quay(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fy = y - ty * T;
  let c = mix(scale(K.concrete, 1.06), K.concrete, fbm(x / 12, y / 12, 551) * 0.7);
  const sx = ((x % 32) + 32) % 32;
  if (sx < 0.9) c = scale(c, 0.74);
  if (hash2(Math.floor(x), Math.floor(y), 552) < 0.05) c = scale(c, 0.9);
  const seaBelow = at(map, tx, ty + 1) === 'Z' || at(map, tx, ty + 1) === 'q';
  if (seaBelow) {
    if (fy > 13.2 && fy < 15.2) {
      // Yellow line with black hatching.
      return ((Math.floor((x + y) / 3) % 4) === 0) ? hex('#2a2a2a') : K.yellow;
    }
    if (fy >= 15.2) return scale(c, 0.55);
    c = scale(c, 1 - smoothstep(6, 13, fy) * 0.12);
  }
  if (at(map, tx - 1, ty) === 'q' || at(map, tx + 1, ty) === 'q') c = scale(c, 0.9);
  return c;
}

/** LRT grass track: turf with two polished steel rails running east-west and a few sleepers showing. */
function grassTrack(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fy = y - ty * T;
  let c = mix(K.turfA, K.turfB, fbm(x / 6, y / 6, 561) * 0.8 + noise2(x, y, 562) * 0.2);
  if (hash2(Math.floor(x), Math.floor(y), 563) < 0.08) c = scale(c, 1.12);
  // The two rails are 16 px apart; the track is two tiles tall so each tile row carries one rail.
  const ry = ty % 2 === 0 ? 11 : 5;
  const d = Math.abs(fy - ry);
  if (d < 0.7) return mix(K.steelShine, K.steel, 0.4 + noise2(x * 0.3, 0, 564) * 0.3);
  if (d < 1.5) return K.steel;
  if (d < 2.6) c = mix(c, hex('#8a7a60'), 0.45);
  // Ballast peeking through between the rails.
  if (fy > 6.5 && fy < 9.5 && ((x % 12) + 12) % 12 < 2.4) c = mix(c, hex('#8a8070'), 0.4);
  // The bridge over the river is handled by 'y'; worn turf where paths cross the track.
  if (at(map, tx, ty - 1) === '@' && at(map, tx, ty + 1) === '@' && hash2(tx, 0, 565) < 0.3) c = mix(c, hex('#a89868'), 0.3);
  return c;
}

/** Liuhe by day and night: dark asphalt, white stall-pitch lines, a red edge line, grease and gum spots. */
function marketStreet(x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  let c = mix(K.asphalt, K.asphaltLight, fbm(x / 7, y / 7, 571) * 0.7);
  if (hash2(Math.floor(x), Math.floor(y), 572) < 0.06) c = scale(c, 1.15);
  const grease = fbm(x / 9, y / 9, 573);
  if (grease > 0.7) c = scale(c, 0.78);
  // Stall pitches: white lines every 3 tiles in the two outer rows.
  const edgeRow = ty === LIUHE[1] || ty === LIUHE[1] + LIUHE[3] - 1;
  if (edgeRow && tx % 3 === 0 && fx < 1.2) return K.white;
  if (ty === LIUHE[1] && fy < 1.3) return K.red;
  if (ty === LIUHE[1] + LIUHE[3] - 1 && fy > 14.7) return K.red;
  if (edgeRow && (ty === LIUHE[1] ? fy > 14.6 : fy < 1.4)) return mix(K.white, c, 0.25);
  // Manhole-ish gum spots.
  if (hash2(Math.floor(x / 3), Math.floor(y / 3), 574) < 0.02) c = scale(c, 1.25);
  return c;
}

/** Flat concrete roof seen from above: parapet rim, gravel, rain stains, a lighter south face. */
function roof(map: MapDef, x: number, y: number): RGB {
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const fx = x - tx * T, fy = y - ty * T;
  let c = mix(K.roof, K.roofDark, fbm(x / 10, y / 10, 581) * 0.6);
  if (hash2(Math.floor(x), Math.floor(y), 582) < 0.1) c = scale(c, 1.08);
  const stain = fbm(x / 6, y / 6, 583);
  if (stain > 0.66) c = scale(c, 0.85);
  const open = (dx: number, dy: number) => at(map, tx + dx, ty + dy) !== '%';
  const rim = (open(0, -1) && fy < 2.2) || (open(0, 1) && fy > T - 2.2) || (open(-1, 0) && fx < 2.2) || (open(1, 0) && fx > T - 2.2);
  if (rim) return mix(K.roofLight, c, 0.25);
  const inner = (open(0, -1) && fy < 3.2) || (open(0, 1) && fy > T - 3.2) || (open(-1, 0) && fx < 3.2) || (open(1, 0) && fx > T - 3.2);
  if (inner) return scale(c, 0.8);
  return c;
}

export function colorAtKaohsiung(map: MapDef, x: number, y: number, mat: string): RGB | null {
  if (mat !== '@' && mat !== '$' && mat !== '%') return null;
  const tx = Math.floor(x / T), ty = Math.floor(y / T);
  const here = map.id === 'kaohsiung';
  switch (mat) {
    case '@':
      if (!here) return pavers(x, y);
      if (inRect(tx, ty, QUAY)) return quay(map, x, y);
      if (inRect(tx, ty, STATION)) return granite(x, y);
      if (inRect(tx, ty, LOTUS)) return brick(map, x, y);
      if (FORMOSA.some((r) => inRect(tx, ty, r))) return terrazzo(x, y);
      if (inRect(tx, ty, PIER2)) return portConcrete(x, y);
      return pavers(x, y);
    case '$':
      if (here && inRect(tx, ty, LIUHE)) return marketStreet(x, y);
      return grassTrack(map, x, y);
    default:
      return roof(map, x, y);
  }
}

/** Flat colours shown while the detailed ground is still rendering. */
export const PLACEHOLDER_KAOHSIUNG: Record<string, string> = { '@': '#d4c4a8', '$': '#5a9044', '%': '#908c86' };
